import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { 
  HiOutlineX, 
  HiOutlineCheck, 
  HiOutlineExclamation,
  HiOutlineDocumentText,
  HiOutlineIdentification,
  HiOutlineClipboardCheck,
  HiOutlineAcademicCap,
  HiOutlineUpload
} from 'react-icons/hi';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { updateUserProfile } from '../../store/slices/authSlice';
import { sendProfileUpdateNotification } from '../../emailjs/emailService';
import './ProfileEditForms.scss';

const DocumentsUploadForm = ({ isOpen, onClose, userData }) => {
  const dispatch = useDispatch();
  const [activeDocType, setActiveDocType] = useState('resume');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add function to handle modal overlay click
  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const docTypes = [
    { 
      id: 'resume',
      name: 'Resume/CV', 
      icon: <HiOutlineDocumentText />,
      fieldName: 'resumeUrl',
      accept: ".pdf,.doc,.docx"
    },
    { 
      id: 'idProof',
      name: 'ID Proof', 
      icon: <HiOutlineIdentification />,
      fieldName: 'idProofUrl',
      accept: ".pdf,.jpg,.jpeg,.png"
    },
    { 
      id: 'expCertificate',
      name: 'Experience Certificate', 
      icon: <HiOutlineClipboardCheck />,
      fieldName: 'expCertificateUrl',
      accept: ".pdf,.jpg,.jpeg,.png"
    },
    { 
      id: 'eduCertificate',
      name: 'Education Certificate', 
      icon: <HiOutlineAcademicCap />,
      fieldName: 'eduCertificateUrl',
      accept: ".pdf,.jpg,.jpeg,.png"
    }
  ];

  const activeDoc = docTypes.find(doc => doc.id === activeDocType);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size exceeds 5MB limit');
        return;
      }
      
      // Check file type based on active document
      const fileExt = file.name.split('.').pop().toLowerCase();
      const allowedExts = activeDoc.accept.split(',').map(ext => ext.replace('.', ''));
      
      if (!allowedExts.includes(fileExt)) {
        setError(`Invalid file type. Allowed types: ${activeDoc.accept}`);
        return;
      }
      
      setSelectedFile(file);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccess('');

    try {
      // Create a storage reference
      const storageRef = ref(storage, `users/${userData.email}/documents/${activeDocType}_${Date.now()}_${selectedFile.name}`);
      
      // Upload file
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);
      
      // Listen for state changes
      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          setError('Failed to upload file. Please try again.');
          setIsUploading(false);
        },
        async () => {
          // Get download URL after successful upload
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          // Update user profile with the document URL
          const updateData = {
            [activeDoc.fieldName]: downloadURL
          };
          
          await dispatch(updateUserProfile({
            email: userData.email,
            profileData: updateData
          })).unwrap();
          
          // Send profile update notification email
          try {
            await sendProfileUpdateNotification(
              userData,
              'documents',
              { [activeDoc.fieldName]: activeDoc.name }
            );
            console.log('Document upload notification sent successfully');
          } catch (emailError) {
            console.error('Failed to send document upload notification:', emailError);
            // Don't fail the whole operation if email fails
          }
          
          setSuccess(`${activeDoc.name} uploaded successfully!`);
          setSelectedFile(null);
          setIsUploading(false);
          setUploadProgress(0);
        }
      );
    } catch (err) {
      console.error('Error handling upload:', err);
      setError(err.message || 'Failed to upload document. Please try again.');
      setIsUploading(false);
    }
  };

  return (
    <div className="profile-edit-modal document-upload-modal">
      <div className="modal-overlay" onClick={handleOverlayClick}></div>
      <div className="modal-container">
        <div className="modal-header">
          <h2>Manage Documents</h2>
          <button className="close-button" onClick={onClose}>
            <HiOutlineX />
          </button>
        </div>

        {error && (
          <div className="alert error">
            <HiOutlineExclamation />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert success">
            <HiOutlineCheck />
            <span>{success}</span>
          </div>
        )}

        <div className="doc-types-nav">
          {docTypes.map(docType => (
            <div 
              key={docType.id} 
              className={`doc-type-item ${activeDocType === docType.id ? 'active' : ''}`}
              onClick={() => {
                setActiveDocType(docType.id);
                setSelectedFile(null);
                setError('');
              }}
            >
              <div className="doc-type-icon">
                {docType.icon}
              </div>
              <div className="doc-type-info">
                <span className="doc-type-name">{docType.name}</span>
                <span className="doc-type-status">
                  {userData[docType.fieldName] ? 'Uploaded' : 'Not uploaded'}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="upload-section">
          <h3>Upload {activeDoc.name}</h3>
          
          {userData[activeDoc.fieldName] && (
            <div className="current-document">
              <p>Current document:</p>
              <div className="doc-preview">
                <div className="doc-icon">
                  {activeDoc.icon}
                </div>
                <div className="doc-actions">
                  <a 
                    href={userData[activeDoc.fieldName]} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="doc-action-btn view"
                  >
                    View
                  </a>
                  <a 
                    href={userData[activeDoc.fieldName]} 
                    download 
                    className="doc-action-btn download"
                  >
                    Download
                  </a>
                </div>
              </div>
            </div>
          )}
          
          <div className="file-upload-wrapper">
            <div className="file-input-container">
              <input
                type="file"
                id="document-file"
                onChange={handleFileChange}
                accept={activeDoc.accept}
                disabled={isUploading}
              />
              <label htmlFor="document-file" className="file-input-label">
                <div className="file-icon">
                  <HiOutlineUpload />
                </div>
                <div className="file-info">
                  <span className="file-prompt">
                    {selectedFile ? selectedFile.name : `Choose ${activeDoc.name} file...`}
                  </span>
                  <span className="file-hint">
                    Supported formats: {activeDoc.accept.replace(/\./g, '')}
                  </span>
                </div>
              </label>
            </div>
            
            {isUploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{Math.round(uploadProgress)}% Uploaded</span>
              </div>
            )}
            
            <div className="upload-actions">
              <button 
                className="upload-button" 
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload Document'}
              </button>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="close-button" 
            onClick={onClose}
            disabled={isUploading}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentsUploadForm; 